import React from 'react';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import PublicPage, { SUPPORT_EMAIL } from './PublicPage';

// Public user guide: adding, using and removing ZPro Audio (required by the Zoom Marketplace).
const Guide = () => (
  <PublicPage title="User Guide" updated="5 October 2026" icon={<MenuBookRoundedIcon sx={{ fontSize: 40, color: '#38bdf8' }} />}>
    <p>
      ZPro Audio turns a Zoom meeting into a hold-to-talk audio room. The organisation's <strong>admin</strong> sets things up in the
      web panel, <strong>hosts</strong> run rooms from the Windows or Android app, and <strong>participants</strong> join from the Android
      or Windows app.
    </p>

    <h2>Adding the app</h2>
    <p>You need a Zoom account (a paid plan if you want cloud recordings) and a ZPro Audio admin login from us.</p>
    <ol>
      <li>Go to <a href="https://zt.zaudio.us/">zt.zaudio.us</a> and log in with your admin username and password.</li>
      <li>On the <strong>Zoom Account</strong> card, click <strong>Connect Zoom</strong>.</li>
      <li>Sign in to Zoom with the account the meetings should run on, review the permissions and click <strong>Allow</strong>.</li>
      <li>You return to the panel and the card shows <strong>Connected</strong>.</li>
    </ol>
    <h3>If adding fails</h3>
    <ul>
      <li><strong>The card still says Not connected:</strong> click Connect Zoom again and make sure you click Allow in Zoom.</li>
      <li><strong>Zoom shows an error page:</strong> your Zoom account admin may restrict which apps users can add. Ask them to allow ZPro Audio.</li>
      <li>Still stuck: email <a href={'mailto:' + SUPPORT_EMAIL}>{SUPPORT_EMAIL}</a>.</li>
    </ul>

    <h2>Using the app</h2>
    <h3>1. Set up rooms and people (admin, web panel)</h3>
    <ul>
      <li><strong>Users → Add User:</strong> create a <em>host</em> for each room, then the <em>participants</em>.</li>
      <li><strong>Rooms → Add Room:</strong> pick the host, name the room and set a password. ZPro Audio creates the room's Zoom meeting on your connected account.</li>
      <li>Assign each participant to a room. Hosts can also add and manage their own room's participants from the panel.</li>
    </ul>
    <h3>2. Install the apps</h3>
    <ul>
      <li>Android (Android 9 or newer): <a href="https://zt.zaudio.us/dl/zpro.apk">download the app</a>.</li>
      <li>Windows 10 or 11: <a href="https://zt.zaudio.us/dl/ZProAudio.exe">download ZProAudio.exe</a> and run it; it adds a desktop shortcut.</li>
    </ul>
    <h3>3. Run a room (host)</h3>
    <ul>
      <li>Log in with the host username and click <strong>Start Meeting</strong>.</li>
      <li>Each participant appears as a tile; the tile turns green while that person talks.</li>
      <li>Controls: <strong>Mute All</strong>, click a tile to mute, remove, block or make co-host, <strong>End Meeting</strong> or <strong>Leave</strong>.</li>
      <li>Windows only: the <strong>Recent speakers</strong> list replays the last few minutes of each speaker.</li>
    </ul>
    <h3>4. Join a room (participant)</h3>
    <ul>
      <li>Log in, wait for <strong>Host is live</strong>, then press <strong>Join</strong>.</li>
      <li>Hold the mic button to talk and let go to listen. The ring turns blue while the mic connects and the button turns white when you are live.</li>
      <li>Participants leave automatically when the host leaves the meeting.</li>
    </ul>
    <h3>5. Recordings</h3>
    <p>If cloud recording is on in your Zoom account, admins and hosts can play the room's recordings under <strong>Cloud Recordings</strong> in the panel; admins can also delete them there.</p>

    <h2>Removing the app</h2>
    <p>There are two ways; either one fully disconnects ZPro Audio from your Zoom account.</p>
    <ol>
      <li><strong>From the panel:</strong> on the Zoom Account card click <strong>Disconnect</strong> and confirm.</li>
      <li><strong>From Zoom:</strong> sign in at <a href="https://marketplace.zoom.us/" target="_blank" rel="noreferrer">marketplace.zoom.us</a>, open <strong>Manage → Added Apps</strong>, find ZPro Audio and click <strong>Remove</strong>.</li>
    </ol>
    <h3>What happens when you remove it</h3>
    <ul>
      <li>We delete your Zoom access and refresh tokens straight away.</li>
      <li>Hosts can no longer start meetings and the panel cannot list recordings until you connect again.</li>
      <li>Meetings already running continue until they end. Your Zoom meetings and cloud recordings stay in your Zoom account; we never copied them.</li>
      <li>Your ZPro Audio users and rooms stay in place so you can reconnect later. To have them erased, email <a href={'mailto:' + SUPPORT_EMAIL}>{SUPPORT_EMAIL}</a>.</li>
    </ul>

    <h2>Troubleshooting</h2>
    <ul>
      <li><strong>"Zoom is disconnected for this account":</strong> the admin must click Connect Zoom in the panel.</li>
      <li><strong>"Already signed in on another device":</strong> each participant account works on one phone; the admin or host can reset the device in the panel.</li>
      <li><strong>Others cannot hear you:</strong> allow microphone access for ZPro Audio in your phone or Windows settings, and hold the button until it turns white.</li>
      <li><strong>Account expired or blocked:</strong> contact your admin.</li>
    </ul>

    <h2>Contact</h2>
    <p>See the <a href="/support">support page</a> for hours, email and phone.</p>
  </PublicPage>
);

export default Guide;
